import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Globe,
  Pencil,
  Play,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { ago } from '../../lib/format.js';
import {
  Badge,
  Button,
  Callout,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Field,
  Input,
  Panel,
  Select,
  Spinner,
  Switch,
  Textarea,
  Tooltip,
  cn,
  useToast,
} from '../../ui/index.js';
import { DataTable } from '../charts.js';
import { ConfirmAction, NewButton } from './forms.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * Webhooks (FR-N8N-04).
 *
 * The screen is built around the two questions somebody actually has about a
 * webhook: is it pointed at the right place, and is it working. So the health
 * of each destination is on the row rather than buried, and "Test" fires a
 * real signed delivery and shows what the endpoint said — the difference
 * between a webhook you have configured and one you know works.
 */

interface Webhook {
  id: string;
  tenant_id: string | null;
  scope: 'tenant' | 'global';
  name: string;
  url: string;
  events: string[];
  active: boolean;
  secretPrefix: string;
  headers: Record<string, string>;
  last_delivered_at: string | null;
  last_failed_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
  delivered: number;
  dead: number;
  created_by_label: string | null;
}

interface Listing {
  webhooks: Webhook[];
  eventTypes: string[];
  mayEditGlobal: boolean;
}

export function WebhooksTab({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const q = useQuery({
    queryKey: ['admin-webhooks', tenant],
    queryFn: () => api.get<Listing>(`/api/v1/tenants/${tenant}/admin/webhooks`),
    enabled: Boolean(tenant),
  });

  const hooks = q.data?.webhooks ?? [];
  const eventTypes = q.data?.eventTypes ?? [];
  const mayEditGlobal = q.data?.mayEditGlobal ?? false;

  return (
    <div className="space-y-4">
      <Panel
        title="Webhooks"
        subtitle="Signed HTTP deliveries for every logical event, from a durable outbox with backoff"
        action={
          <WebhookForm
            tenant={tenant}
            eventTypes={eventTypes}
            mayEditGlobal={mayEditGlobal}
            trigger={<NewButton>New webhook</NewButton>}
          />
        }
        footer={
          <div className="space-y-1.5 text-2xs leading-relaxed text-ink-tertiary">
            <p>
              Deliveries are queued in the same transaction as the change that caused them, so a
              slow or unreachable endpoint can never block or lose a ticket operation. Each
              destination retries on its own schedule and dead-letters after eight attempts —
              one broken receiver does not hold up another.
            </p>
            <p>
              Verify a delivery with{' '}
              <code className="rounded-xs bg-surface px-1 font-mono">
                HMAC-SHA256(secret, "&lt;X-ESA-Timestamp&gt;.&lt;body&gt;")
              </code>{' '}
              and compare against <code className="rounded-xs bg-surface px-1 font-mono">X-ESA-Signature</code>.
              The timestamp is inside the signed material on purpose: a signature over the body
              alone would stay valid forever and could be replayed.
            </p>
          </div>
        }
      >
        {q.isLoading && <Spinner />}
        {q.error && (
          <p className="text-xs text-ink-tertiary">The webhook list could not be loaded.</p>
        )}
        {q.data && hooks.length === 0 && (
          <p className="py-6 text-center text-xs text-ink-tertiary">
            No webhooks yet. Events are still recorded, so anything added later can be replayed
            from the outbox.
          </p>
        )}

        {hooks.length > 0 && (
          <ul className="space-y-2">
            {hooks.map((w) => {
              const readOnly = w.scope === 'global' && !mayEditGlobal;
              const failing = w.consecutive_failures > 0;
              return (
                <li
                  key={w.id}
                  className={cn(
                    'group rounded-md border p-3',
                    failing
                      ? 'border-danger-line/60 bg-danger-tint/25'
                      : 'border-line-subtle bg-surface-sunken/40',
                  )}
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium text-ink">{w.name}</span>
                    {w.scope === 'global' && (
                      <Tooltip content="Receives events from every group. Only a platform admin can change it.">
                        <span>
                          <Badge tone="info" icon={<Globe />}>
                            all groups
                          </Badge>
                        </span>
                      </Tooltip>
                    )}
                    {!w.active && <Badge tone="neutral">paused</Badge>}
                    {failing ? (
                      <Badge tone="danger" icon={<XCircle />}>
                        {w.consecutive_failures} failed in a row
                      </Badge>
                    ) : w.last_delivered_at ? (
                      <Badge tone="ok" icon={<CheckCircle2 />}>
                        delivered {ago(w.last_delivered_at)}
                      </Badge>
                    ) : (
                      <Badge tone="neutral">never fired</Badge>
                    )}
                    {w.dead > 0 && (
                      <Tooltip content="Gave up after eight attempts. The events are still in the outbox.">
                        <span>
                          <Badge tone="warn" icon={<AlertTriangle />}>
                            {w.dead} dead-lettered
                          </Badge>
                        </span>
                      </Tooltip>
                    )}

                    <span className="ml-auto flex items-center gap-0.5 opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                      <TestButton tenant={tenant} id={w.id} />
                      {!readOnly && (
                        <>
                          <WebhookForm
                            tenant={tenant}
                            eventTypes={eventTypes}
                            mayEditGlobal={mayEditGlobal}
                            existing={w}
                                            trigger={
                              <Button variant="ghost" size="sm" iconOnly aria-label={`Edit ${w.name}`}>
                                <Pencil aria-hidden className="h-3.5 w-3.5" />
                              </Button>
                            }
                          />
                          <ConfirmAction
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                aria-label={`Delete ${w.name}`}
                              >
                                <Trash2 aria-hidden className="h-3.5 w-3.5" />
                              </Button>
                            }
                            title={`Delete "${w.name}"?`}
                            body={
                              <>
                                Deliveries stop immediately and queued ones are dropped. Events
                                themselves are still recorded, so a replacement can be replayed
                                from the outbox. To stop it temporarily, pause it instead.
                              </>
                            }
                            confirmLabel="Delete webhook"
                            pending={write.isPending}
                            onConfirm={() =>
                              write.mutateAsync({
                                method: 'del',
                                path: `/api/v1/tenants/${tenant}/admin/webhooks/${w.id}`,
                                done: `"${w.name}" deleted`,
                              })
                            }
                          />
                        </>
                      )}
                    </span>
                  </div>

                  <p className="mt-1.5 truncate font-mono text-2xs text-ink-secondary">{w.url}</p>

                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    {w.events.map((e) => (
                      <Badge key={e} tone="neutral">
                        {e}
                      </Badge>
                    ))}
                  </div>

                  <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-3xs text-ink-tertiary">
                    <span>
                      secret <code className="font-mono">{w.secretPrefix}</code>
                    </span>
                    <span>{w.delivered} delivered</span>
                    {w.created_by_label && <span>added by {w.created_by_label}</span>}
                    {readOnly && <span>read-only — managed at platform level</span>}
                  </p>

                  {w.last_error && (
                    <p className="mt-2 rounded-xs bg-surface px-2 py-1 font-mono text-3xs leading-relaxed text-danger">
                      {w.last_error}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/** Fire a real signed delivery and show exactly what came back. */
function TestButton({ tenant, id }: { tenant: string; id: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  return (
    <Tooltip content="Send a signed test delivery now">
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        loading={busy}
        aria-label="Send a test delivery"
        onClick={() => {
          setBusy(true);
          void api
            .post<{ ok: boolean; status: number | null; ms: number; responseBody?: string; error?: string }>(
              `/api/v1/tenants/${tenant}/admin/webhooks/${id}/test`,
            )
            .then((r) => {
              if (r.ok) {
                toast.success(`Endpoint answered ${r.status} in ${r.ms}ms`, 'Signature sent and accepted.');
              } else {
                toast.error(
                  r.status === null ? 'Could not reach the endpoint' : `Endpoint answered ${r.status}`,
                  r.error ?? r.responseBody ?? undefined,
                );
              }
            })
            .catch((err: unknown) =>
              toast.error(err instanceof ApiError ? err.message : 'The test could not be sent'),
            )
            .finally(() => setBusy(false));
        }}
      >
        <Play aria-hidden className="h-3.5 w-3.5" />
      </Button>
    </Tooltip>
  );
}

function WebhookForm({
  tenant,
  eventTypes,
  mayEditGlobal,
  existing,
  trigger,
}: {
  tenant: string;
  eventTypes: string[];
  mayEditGlobal: boolean;
  existing?: Webhook;
  trigger: React.ReactNode;
}) {
  const write = useConfigWrite(tenant);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(existing?.name ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [scope, setScope] = useState<'tenant' | 'global'>(existing?.scope ?? 'tenant');
  const [events, setEvents] = useState<string[]>(existing?.events ?? ['ticket.*']);
  const [active, setActive] = useState(existing?.active ?? true);
  const [headers, setHeaders] = useState(
    Object.entries(existing?.headers ?? {})
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n'),
  );
  /** Shown once, after creation. The server will never return it again. */
  const [secret, setSecret] = useState<string | null>(null);

  const urlOk = /^https?:\/\/.+/i.test(url);
  const nameOk = name.trim().length >= 2;
  const ready = urlOk && nameOk && events.length > 0;

  const parseHeaders = (): Record<string, string> =>
    Object.fromEntries(
      headers
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const i = l.indexOf(':');
          return i === -1 ? [l, ''] : [l.slice(0, i).trim(), l.slice(i + 1).trim()];
        }),
    );

  const toggle = (e: string) =>
    setEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));

  const submit = async () => {
    const body = {
      name: name.trim(),
      url: url.trim(),
      events,
      headers: parseHeaders(),
      active,
      ...(existing ? {} : { scope }),
    };
    const res = (await write.mutateAsync({
      method: existing ? 'patch' : 'post',
      path: existing
        ? `/api/v1/tenants/${tenant}/admin/webhooks/${existing.id}`
        : `/api/v1/tenants/${tenant}/admin/webhooks`,
      body,
      done: existing ? `"${body.name}" saved` : `"${body.name}" created`,
    })) as { secret?: string };

    // The secret is returned exactly once. Do not close over it.
    if (!existing && res?.secret) setSecret(res.secret);
    else setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSecret(null);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        title={existing ? `Webhook · ${existing.name}` : 'New webhook'}
        description={
          secret
            ? 'Copy the signing secret now — this is the only time it is shown.'
            : 'Events are delivered from a durable outbox with backoff, signed so the receiver can prove they came from here.'
        }
        width="lg"
      >
        {secret ? (
          <div className="space-y-3">
            <Callout tone="warn" icon={<AlertTriangle />}>
              This is the only time this secret is shown. The server keeps it to sign with, and
              will never return it again — if it is lost, rotate it by recreating the webhook.
            </Callout>
            <Field label="Signing secret">
              <div className="flex gap-2">
                <Input value={secret} readOnly className="font-mono" />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(secret)
                      .then(() => toast.success('Secret copied'))
                      .catch(() => toast.error('Could not copy — select it and copy manually'));
                  }}
                >
                  <Copy aria-hidden className="h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
            </Field>
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              Verify each delivery as{' '}
              <code className="font-mono">HMAC-SHA256(secret, "&lt;X-ESA-Timestamp&gt;.&lt;body&gt;")</code>{' '}
              and compare with the <code className="font-mono">X-ESA-Signature</code> header. Reject
              anything older than five minutes.
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => setOpen(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit().catch(() => undefined);
            }}
          >
            <div className="space-y-3">
              <Field label="Name" hint="How you will recognise it in this list" required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="n8n — ticket automation"
                />
              </Field>

              <Field
                label="URL"
                hint="Where the POST goes. http or https."
                error={url.length > 0 && !urlOk ? 'Must be an http or https URL' : undefined}
                required
              >
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://automation.esassoc.com/webhook/tributary"
                  className="font-mono"
                />
              </Field>

              {!existing && (
                <Field
                  label="Scope"
                  hint={
                    scope === 'global'
                      ? 'Receives events from EVERY group, not just this one.'
                      : 'Receives events from this group only.'
                  }
                >
                  <Select
                    value={scope}
                    onValueChange={(v) => setScope(v as 'tenant' | 'global')}
                    options={[
                      { value: 'tenant', label: 'This group only' },
                      {
                        value: 'global',
                        label: 'Every group',
                        hint: mayEditGlobal ? 'Platform-wide' : 'Requires platform admin',
                        disabled: !mayEditGlobal,
                      },
                    ]}
                    aria-label="Scope"
                  />
                </Field>
              )}

              <div>
                <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">
                  Events
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {['*', 'ticket.*', 'sla.*', ...eventTypes].map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => toggle(e)}
                      className={cn(
                        'rounded-full px-2 py-0.5 text-2xs transition-colors',
                        events.includes(e)
                          ? 'bg-brand text-white'
                          : 'bg-surface-sunken text-ink-secondary hover:bg-surface-hover',
                      )}
                    >
                      {e}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-3xs leading-relaxed text-ink-tertiary">
                  A wildcard keeps working when a new event type ships. Listing every type
                  individually means a subscription silently stops covering what you intended the
                  moment a tenth one is added.
                </p>
              </div>

              <Field
                label="Extra headers"
                hint="One per line, «Name: value». For a receiver with its own auth. X-ESA-* is reserved."
              >
                <Textarea
                  rows={2}
                  value={headers}
                  onChange={(e) => setHeaders(e.target.value)}
                  placeholder="Authorization: Bearer …"
                  className="font-mono"
                />
              </Field>

              <Switch checked={active} onCheckedChange={setActive} label="Active" />

              {scope === 'global' && !existing && (
                <Callout tone="warn" icon={<Globe />}>
                  A global webhook receives every group's ticket events, including groups you do
                  not administer.
                </Callout>
              )}
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" loading={write.isPending} disabled={!ready}>
                {existing ? 'Save webhook' : 'Create webhook'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Recent deliveries, for when a receiver says nothing arrived. */
export function DeliveryLog({ tenant, id }: { tenant: string; id: string }) {
  const q = useQuery({
    queryKey: ['webhook-deliveries', tenant, id],
    queryFn: () =>
      api.get<{
        deliveries: {
          event_type: string;
          attempts: number;
          delivered_at: string | null;
          dead_at: string | null;
          last_error: string | null;
          created_at: string;
        }[];
      }>(`/api/v1/tenants/${tenant}/admin/webhooks/${id}/deliveries`),
  });
  if (!q.data) return <Spinner />;
  return (
    <DataTable
      head={['Event', 'Queued', 'Attempts', 'Outcome']}
      rows={q.data.deliveries.map((d) => [
        d.event_type,
        ago(d.created_at),
        String(d.attempts),
        d.delivered_at ? 'delivered' : d.dead_at ? `gave up — ${d.last_error ?? ''}` : 'queued',
      ])}
    />
  );
}
